<script setup>
import { ref, onMounted, watch, computed, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import Spinner from '../../../../../components-next/spinner/Spinner.vue';
import Button from '../../../../../components-next/button/Button.vue';
import ValidationFeedback from '../shared/ValidationFeedback.vue';

const { t } = useI18n();
const store = useStore();
const emit = defineEmits(['select']);

const props = defineProps({
  initialPersonalityId: {
    type: Number,
    default: null
  },
  companyName: {
    type: String,
    default: ''
  },
  agentName: {
    type: String,
    default: ''
  },
  // Nova prop para detectar mudança de passo
  currentStep: {
    type: Number,
    default: null
  }
});

const personalities = computed(() => [
  {
    id: 1,
    name: 'Profissional',
    description: 'Um estilo formal e profissional, ideal para atendimento corporativo.',
    example: `Olá, sou ${props.agentName || '[Nome do Agente]'} da ${props.companyName || '[Nome da Empresa]'}. Como posso ajudá-lo hoje?`,
    traits: ['Formal', 'Objetivo', 'Cortês'],
    preview: {
      greeting: 'Bom dia! Sou Sofia, assistente virtual. Como posso auxiliá-lo hoje?',
      response: 'Certamente posso ajudá-lo com isso. Temos soluções que atendem exatamente suas necessidades. Permita-me apresentar as opções disponíveis e encontrar a melhor alternativa para você.',
      closing: 'Foi um prazer atendê-lo. Caso tenha outras dúvidas, estarei à disposição. Tenha um excelente dia!'
    }
  },
  {
    id: 2,
    name: 'Amigável',
    description: 'Um estilo casual e acolhedor, ótimo para marcas mais descontraídas.',
    example: `Oi! Que bom ver você por aqui! Sou ${props.agentName || '[Nome do Agente]'} da ${props.companyName || '[Nome da Empresa]'}. Como posso te ajudar hoje? 😊`,
    traits: ['Descontraído', 'Empático', 'Acessível'],
    preview: {
      greeting: 'Oi! 👋 Eu sou a Sofia! Que bom te ver por aqui! Como posso te ajudar hoje?',
      response: 'Ah, que legal! Tenho certeza que vamos encontrar algo perfeito pra você! 😊 Temos várias opções incríveis. Deixa eu te mostrar o que combina mais com o que você tá procurando!',
      closing: 'Foi super legal conversar com você! Se precisar de mais alguma coisa, é só chamar que eu tô aqui, tá? Até logo! ✨'
    }
  },
  {
    id: 3,
    name: 'Consultivo',
    description: 'Um estilo focado em orientação e consultoria, ideal para serviços complexos.',
    example: `Bem-vindo à ${props.companyName || '[Nome da Empresa]'}. Sou ${props.agentName || '[Nome do Agente]'} especialista em [Seus Serviços] e estou aqui para fornecer orientações personalizadas. Qual sua principal necessidade hoje?`,
    traits: ['Especialista', 'Detalhista', 'Orientador'],
    preview: {
      greeting: 'Bem-vindo! Sou Sofia, sua consultora especializada. Para oferecer a orientação mais adequada, poderia me contar sobre suas necessidades específicas?',
      response: 'Excelente! Baseado no que você me relatou, vou apresentar uma análise detalhada das opções disponíveis. Considere estes pontos importantes: primeiro, vamos avaliar sua situação atual, depois identificar a solução ideal e por fim, definir o melhor plano de ação.',
      closing: 'Espero que estas informações tenham esclarecido suas dúvidas. Preparei um resumo personalizado das recomendações. Fico à disposição para aprofundarmos qualquer ponto. Até breve!'
    }
  }
]);

const selectedPersonalityId = ref(props.initialPersonalityId);
const error = ref(null);
const showPreview = ref(false);
const previewPersonality = ref(null);
const hasInteracted = ref(false);
const currentMessageIndex = ref(0);
const isTyping = ref(false);

// Array para armazenar todos os timeouts ativos
const activeTimeouts = ref([]);

// Função para limpar todos os timeouts
const clearAllTimeouts = () => {
  activeTimeouts.value.forEach(timeout => clearTimeout(timeout));
  activeTimeouts.value = [];
};

// Função para resetar o estado do preview
const resetPreviewState = () => {
  clearAllTimeouts();
  showPreview.value = false;
  previewPersonality.value = null;
  currentMessageIndex.value = 0;
  isTyping.value = false;
};

// Mensagens da conversa
const conversationMessages = computed(() => {
  if (!previewPersonality.value) return [];
  
  return [
    { type: 'agent', text: previewPersonality.value.preview.greeting, delay: 500 },
    { type: 'client', text: 'Olá! Gostaria de saber mais sobre os serviços que vocês oferecem.', delay: 2000 },
    { type: 'agent', text: previewPersonality.value.preview.response, delay: 3500 },
    { type: 'client', text: 'Obrigado pelas informações! Por enquanto é isso.', delay: 5500 },
    { type: 'agent', text: previewPersonality.value.preview.closing, delay: 7000 }
  ];
});

// Função para mostrar preview
const togglePreview = (personality) => {
  // Limpa timeouts anteriores sempre
  clearAllTimeouts();
  
  if (previewPersonality.value?.id === personality.id && showPreview.value) {
    // Se está fechando o mesmo preview
    resetPreviewState();
  } else {
    // Se está abrindo um preview (novo ou mesmo)
    previewPersonality.value = personality;
    showPreview.value = true;
    currentMessageIndex.value = 0;
    isTyping.value = false;
    
    // Sempre inicia a animação ao abrir
    setTimeout(() => startConversation(), 200);
  }
};

// Função para fechar preview (usada pelo botão X)
const closePreview = () => {
  resetPreviewState();
};

// Função para iniciar a conversa animada
const startConversation = () => {
  if (!showPreview.value) return;
  
  const messages = conversationMessages.value;
  let index = 0;
  
  const showNextMessage = () => {
    // Verifica se o preview ainda está aberto antes de continuar
    if (!showPreview.value || index >= messages.length) return;
    
    const message = messages[index];
    
    // Mostra indicador de digitação para mensagens do agente
    if (message.type === 'agent') {
      isTyping.value = true;
      
      // Armazena o timeout para poder cancelá-lo depois
      const typingTimeout = setTimeout(() => {
        if (!showPreview.value) return; // Verifica novamente
        
        isTyping.value = false;
        currentMessageIndex.value = index + 1;
        index++;
        
        // Próxima mensagem
        const nextTimeout = setTimeout(() => {
          if (showPreview.value) showNextMessage();
        }, 700);
        activeTimeouts.value.push(nextTimeout);
      }, 1000);
      
      activeTimeouts.value.push(typingTimeout);
    } else {
      currentMessageIndex.value = index + 1;
      index++;
      
      // Calcula o delay para a próxima mensagem
      const delay = message.delay - (index > 0 ? messages[index - 1].delay : 0);
      const messageTimeout = setTimeout(() => {
        if (showPreview.value) showNextMessage();
      }, delay);
      
      activeTimeouts.value.push(messageTimeout);
    }
  };
  
  showNextMessage();
};

// Computed para obter a personalidade selecionada
const selectedPersonality = computed(() => {
  return personalities.value.find(p => p.id === selectedPersonalityId.value);
});

const selectPersonality = (id) => {
  selectedPersonalityId.value = id;
  hasInteracted.value = true;
  emit('select', id);
};

// Observa mudanças na prop initialPersonalityId
watch(() => props.initialPersonalityId, (newId) => {
  if (newId !== selectedPersonalityId.value) {
    selectedPersonalityId.value = newId;
  }
});

// Observa mudanças no currentStep para resetar o preview
watch(() => props.currentStep, () => {
  resetPreviewState();
});

// Se já houver uma personalidade selecionada na montagem, marca como interagido
onMounted(() => {
  if (props.initialPersonalityId) {
    hasInteracted.value = true;
  }
});

// Limpa todos os timeouts quando o componente for destruído
onBeforeUnmount(() => {
  clearAllTimeouts();
});

// Expõe o método de reset para o componente pai poder chamar se necessário
defineExpose({
  resetPreviewState
});
</script>

<template>
  <div>
    <!-- Mensagem de erro quando nenhuma personalidade está selecionada -->
    <ValidationFeedback 
      :error="!selectedPersonalityId && hasInteracted ? 'Por favor, selecione uma personalidade para o agente' : ''"
      :touched="hasInteracted"
      :show-helper="false"
      class="mb-4"
    />

    <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div v-for="personality in personalities" :key="personality.id"
        class="bg-n-solid-2 rounded-2xl shadow-sm cursor-pointer hover:shadow-md transition-all duration-200 border-2"
        :class="{ 
          'border-n-brand': selectedPersonalityId === personality.id,
          'border-n-slate-3': selectedPersonalityId !== personality.id 
        }"
        @click="selectPersonality(personality.id)">
        <div class="flex flex-col h-full px-6 py-5">
          <div class="flex flex-col p-4 h-full">
            <div class="flex items-center justify-between mb-2">
              <h4 class="text-lg font-medium text-n-slate-12">{{ personality.name }}</h4>
              <div v-if="selectedPersonalityId === personality.id"
                class="rounded-full bg-n-brand text-white p-1 w-6 h-6 flex items-center justify-center">
                <span class="i-lucide-check text-sm" />
              </div>
            </div>

            <p class="text-sm text-n-slate-11 mb-4 flex-grow">{{ personality.description }}</p>

            <div class="pt-2 border-t border-n-slate-3">
              <h5 class="text-sm font-medium mb-2 text-n-slate-12">Exemplo de Resposta:</h5>
              <p class="text-sm italic text-n-slate-11">{{ personality.example }}</p>
              
              <!-- Características -->
              <div class="mt-3 flex flex-wrap gap-1">
                <span v-for="trait in personality.traits" :key="trait"
                  class="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-n-slate-3 text-n-slate-11">
                  {{ trait }}
                </span>
              </div>
            </div>
            
            <!-- Botão de Preview -->
            <div class="flex-grow"></div>
            <Button
              size="sm"
              variant="outline"
              color="slate"
              label="Ver Preview"
              icon="i-lucide-eye"
              class="mt-3 w-full"
              @click.stop="togglePreview(personality)"
            />
          </div>
        </div>
      </div>
    </div>
    
    <!-- Preview Interativo -->
    <div v-if="showPreview && previewPersonality" 
      class="mt-6 bg-white dark:bg-slate-800 rounded-2xl shadow-lg border border-n-slate-3 overflow-hidden">
      <!-- Header do Chat -->
      <div class="bg-n-slate-2 px-4 py-3 border-b border-n-slate-3">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 rounded-full bg-n-brand flex items-center justify-center text-white font-medium">
              S
            </div>
            <div>
              <h3 class="text-sm font-semibold text-n-slate-12">Sofia - {{ previewPersonality.name }}</h3>
              <p class="text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
                <span class="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                Online
              </p>
            </div>
          </div>
          <Button
            size="sm"
            variant="ghost"
            color="slate"
            icon="i-lucide-x"
            @click="closePreview"
          />
        </div>
      </div>
      
      <!-- Área de Mensagens -->
      <div class="bg-n-slate-1 p-4 overflow-y-auto">
        <div class="space-y-3">
          <!-- Timestamp -->
          <div v-if="currentMessageIndex > 0" class="text-center text-xs text-n-slate-9 my-2">
            Hoje, {{ new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) }}
          </div>
          
          <!-- Mensagens Animadas -->
          <template v-for="(message, index) in conversationMessages" :key="index">
            <div v-if="index < currentMessageIndex" 
              class="flex items-end gap-2 animate-fade-in"
              :class="message.type === 'client' ? 'justify-end' : ''">
              
              <!-- Avatar do Agente -->
              <div v-if="message.type === 'agent'" 
                class="w-7 h-7 rounded-full bg-n-brand flex items-center justify-center text-white text-xs font-medium flex-shrink-0">
                S
              </div>
              
              <!-- Conteúdo da Mensagem -->
              <div class="max-w-[70%]">
                <div :class="message.type === 'agent' 
                  ? 'bg-n-slate-3 dark:bg-slate-800 rounded-2xl rounded-bl-sm' 
                  : 'bg-n-brand text-white rounded-2xl rounded-br-sm'"
                  class="px-4 py-2.5 shadow-sm">
                  <p class="text-sm" :class="message.type === 'agent' ? 'text-n-slate-12' : ''">
                    {{ message.text }}
                  </p>
                </div>
                <p class="text-xs text-n-slate-9 mt-1" 
                  :class="message.type === 'agent' ? 'ml-2' : 'mr-2 text-right'">
                  {{ new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) }}
                </p>
              </div>
            </div>
          </template>
          
          <!-- Indicador de Digitação -->
          <div v-if="isTyping && currentMessageIndex < conversationMessages.length" 
            class="flex items-end gap-2 animate-fade-in">
            <div class="w-7 h-7 rounded-full bg-n-brand flex items-center justify-center text-white text-xs font-medium flex-shrink-0">
              S
            </div>
            <div class="bg-n-slate-3 dark:bg-slate-800 rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm">
              <div class="flex gap-1">
                <span class="w-2 h-2 bg-n-slate-6 rounded-full animate-bounce" style="animation-delay: 0ms"></span>
                <span class="w-2 h-2 bg-n-slate-6 rounded-full animate-bounce" style="animation-delay: 150ms"></span>
                <span class="w-2 h-2 bg-n-slate-6 rounded-full animate-bounce" style="animation-delay: 300ms"></span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
    </div>
  </div>
</template>

<style scoped>
@keyframes fade-in {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fade-in {
  animation: fade-in 0.3s ease-out;
}
</style>